import { LoaderBar, SendEmailModal, PlanUsageStrip } from 'components'
import { GetCustomerList, GetTempleteList, useEmptyAccountTemplateRedirect } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { TemplateList, TemplateNameModal, TemplateListHeader } from 'page-components'
import { useState } from 'react'
import { InspectionService, TemplateService, AuthService, isPlanLimitErrorCode, getPlanLimitCode } from 'utility'
import { useNotification } from 'hooks/toast'
import { useSubscriptionUsage } from 'context/subscription-usage'
import { getSavedTemplateSort, saveTemplateSort } from 'utility/template-list-preferences'

export const TemplateListScreen = () => {
    const user = AuthService.getUser()
    const isWKCompany = user?.CompanyId === 34930
    const { refetchUsage, isFreePlan } = useSubscriptionUsage()

    // WK Audit Template list uses original API (no TemplateInternalType).
    // The sort the user picked last time (this browser) is used from the first load; read once per visit.
    const [initialSort] = useState(() => getSavedTemplateSort(user?.unique_id))
    const {
        data,
        loading,
        filter,
        pageChanged,
        totalCount,
        setData,
        filterChanged,
    } = GetTempleteList(null, null, { SortBy: initialSort })
    const { successToast } = useNotification()
    const [processing, setProcessing] = useState('')
    const [search, setSearch] = useState('')
    const navigate = useNavigate()
    const [emailModalOpen, setEmailModalOpen] = useState(false)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [showTemplateNameModal, setShowTemplateNameModal] = useState(false)
    const [creating, setCreating] = useState(false)
    // Pass CallFrom=1 so the API only changes for this page
    const { data: customerList, loading: customerLoading, refresh: refreshCustomerList } = GetCustomerList({ CallFrom: 1 });

    const headerText = isWKCompany ? 'WK Audit Template list' : 'Template List'

    useEmptyAccountTemplateRedirect({ loading, data })

    const handleSearchClick = () => {
        const trimmedSearch = search.trim()

        if (trimmedSearch === '') {
            filterChanged({ SearchText: '' })
            return
        }

        filterChanged({ SearchText: trimmedSearch })
    }

    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            handleSearchClick()
        }
    }

    const onRemove = async item => {
        try {
            setProcessing('Processing')
            await TemplateService.removeTemplate(item.TemplateId)
            setData(data.filter(ele => ele.TemplateId !== item.TemplateId))
            successToast('Template deleted successfully')
            refetchUsage()
        } catch (error) {
            console.log(error)
        } finally {
            setProcessing('')
        }
    }

    const startInspection = async (item, list) => {
        try {
            const result = await InspectionService.copyInspection({
                TemplateId: item.TemplateId,
                customerId: list.toString(),
            })
            navigate(`inspection/${result?.AuditTemplateId}`)
        } catch (error) {
            console.log(error)
        } finally {
            setProcessing('')
        }
    }

    const handleSendEmail = (template) => {
        setSelectedTemplate(template)
        setEmailModalOpen(true)
    }

    const handleEmailSuccess = () => {
        // Optional: Show success message
    }

    const handleOpenCreateModal = () => {
        setShowTemplateNameModal(true)
    }

    const handleCreateTemplate = async (templateName) => {
        setCreating(true)
        try {
            const result = await TemplateService.create({
                TemplateId: '-1',
                TemplateName: templateName,
                LoginSource: 1,
                TemplateAccess: 'Internal',
            })
            refetchUsage()
            navigate('/app/templates/create', { state: { template: result } })
        } catch (error) {
            const data = error?.error || {}
            const code = getPlanLimitCode(data)
            // Global PlanLimitBlockDialog handles PLAN_* 403s.
            if (!(error?.status === 403 && isPlanLimitErrorCode(code))) {
                console.error('Failed to create template:', error)
            }
        } finally {
            setCreating(false)
            setShowTemplateNameModal(false)
        }
    }

    return (
        <>
            <TemplateListHeader
                search={search}
                setSearch={setSearch}
                handleSearchClick={handleSearchClick}
                handleKeyPress={handleKeyPress}
                onCreateClick={handleOpenCreateModal}
                headerText={headerText}
                totalCount={loading ? undefined : totalCount}
            />
            {isFreePlan && (
                <PlanUsageStrip metric="templates" showUpgrade />
            )}
            <div className="my-3">
                {(processing || loading) && (
                    <LoaderBar content={processing || 'Loading'} />
                )}
                <TemplateList
                    data={data}
                    pageChanged={pageChanged}
                    totalCount={totalCount}
                    filter={filter}
                    customerList={customerList}
                    customerLoading={customerLoading}
                    onRemove={onRemove}
                    startInspection={startInspection}
                    onSendEmail={handleSendEmail}
                    refreshCustomerList={refreshCustomerList}
                    sortBy={filter.SortBy}
                    onSortChange={value => {
                        saveTemplateSort(user?.unique_id, value)
                        filterChanged({ SortBy: value, PageNumber: 1 })
                    }}
                />
            </div>
            {emailModalOpen && selectedTemplate && (
                <SendEmailModal
                    open={emailModalOpen}
                    onClose={() => setEmailModalOpen(false)}
                    template={selectedTemplate}
                    onSuccess={handleEmailSuccess}
                />
            )}
            <TemplateNameModal
                open={showTemplateNameModal}
                onClose={() => setShowTemplateNameModal(false)}
                onSubmit={handleCreateTemplate}
                loading={creating}
            />
        </>
    )
}
